export type WorkspaceProvider = "sprites" | "gcp_compute" | "e2b" | "daytona" | "lambda_microvms";

export type ProjectOption = {
  id: string;
  name: string;
};

export type WorkspaceRef = {
  provider: WorkspaceProvider;
  resourceId: string;
  projectIds: string[];
  ownerUserId: string;
  shared: boolean;
  updatedAt: number;
  meta?: Record<string, unknown>;
};

export type StarterProjectChoice =
  | { kind: "starter"; kitId: string; kitName: string; keepGit?: boolean }
  | { kind: "blank" };

export type OnboardingDraft = {
  agentId: string | null;
  projectId: string | null;
  projectName: string;
  starterProject: StarterProjectChoice | null;
  workspaceMode: "existing" | "create";
  existingWorkspaceKey: string | null;
  createProvider: WorkspaceProvider;
  createShared: boolean;
};

export type OnboardingSelection = {
  agentId: string;
  project: ProjectOption;
  projectName: string;
  starterProject: StarterProjectChoice;
  workspace:
    | { kind: "existing"; workspace: WorkspaceRef }
    | { kind: "create"; provider: WorkspaceProvider; shared: boolean };
};

export type BootStage =
  | "creating_workspace"
  | "waiting_for_workspace"
  | "waiting_for_terminal"
  | "installing_starter_project"
  | "installing_agent"
  | "starting_agent";

export type BootTimingContext = {
  provider?: WorkspaceProvider;
  workspaceKind?: "existing" | "create";
  agentId?: string | null;
};

type BootStageConfig = {
  headline: string;
  detail: string;
  estimateMs: (context: Required<BootTimingContext>) => number;
};

export type BootStageState = {
  stage: BootStage;
  progress: number;
  progressStart: number;
  progressEnd: number;
  expectedMs: number;
  startedAt: number;
  headline: string;
  detail: string;
};

function withDefaults(context: BootTimingContext): Required<BootTimingContext> {
  return {
    provider: context.provider || "gcp_compute",
    workspaceKind: context.workspaceKind || "existing",
    agentId: context.agentId || null,
  };
}

// Measured from prod [timing] logs across both deployments:
//
//   inner-replica (australia-southeast1):
//     workspace-create  43s, workspace-ready 83s, terminal 3s
//     agent-install: claude 13s, gemini 63s, codex 15s
//     agent-start:   claude 8s,  gemini 13s, codex 8s
//
//   path26 (europe-west1):
//     workspace-create  37s, workspace-ready ~0s, terminal 3s
//     agent-install: claude 13s, gemini 63s
//     agent-start:   claude 8s,  gemini 13s
//
// Progress ranges are computed dynamically from these estimates so the bar
// moves at a steady rate regardless of agent or region. When a stage finishes
// faster than estimated the bar jumps forward, but only by the proportion of
// time that stage was expected to take — no more 47% leaps.

function estimateCreateWorkspaceMs(context: Required<BootTimingContext>): number {
  if (context.provider === "sprites") {
    return 20_000;
  }
  if (context.provider === "e2b") {
    return 20_000;
  }
  return context.workspaceKind === "create" ? 40_000 : 12_000;
}

function estimateWorkspaceReadyMs(context: Required<BootTimingContext>): number {
  if (context.provider === "sprites") {
    return context.workspaceKind === "create" ? 4_000 : 2_000;
  }
  // GCE cold starts: instance boot + startup script + freshvibe-core health can still take around 90s.
  // Warm starts are near-instant. Use a high estimate for create so the progress bar
  // doesn't stall; the ×2 multiplier in buildBootStage gives extra headroom.
  return context.workspaceKind === "create" ? 60_000 : 2_000;
}

function estimateTerminalReadyMs(context: Required<BootTimingContext>): number {
  if (context.provider === "sprites") {
    return context.workspaceKind === "create" ? 4_000 : 2_500;
  }
  return context.workspaceKind === "create" ? 3_000 : 2_000;
}

function estimateAgentInstallMs(context: Required<BootTimingContext>): number {
  switch (context.agentId) {
    case "gemini":
      return 60_000;
    case "codex":
    case "claude":
    case "copilot":
      return 15_000;
    case "opencode":
      return 8_000;
    default:
      return 15_000;
  }
}

function estimateAgentStartMs(context: Required<BootTimingContext>): number {
  switch (context.agentId) {
    case "gemini":
      return 13_000;
    case "codex":
      return 8_000;
    case "claude":
    case "copilot":
      return 8_000;
    case "opencode":
      return 5_000;
    default:
      return 8_000;
  }
}

const BOOT_PROGRESS_START = 4;
const BOOT_PROGRESS_END = 97;

const STAGE_ORDER: BootStage[] = [
  "creating_workspace",
  "waiting_for_workspace",
  "waiting_for_terminal",
  "installing_starter_project",
  "installing_agent",
  "starting_agent",
];

const STAGE_CONFIG: Record<BootStage, BootStageConfig> = {
  creating_workspace: {
    headline: "Creating your workspace",
    detail: "Provisioning the runtime and connecting storage for your project.",
    estimateMs: estimateCreateWorkspaceMs,
  },
  waiting_for_workspace: {
    headline: "Preparing the workspace",
    detail: "Waiting for the workspace to finish booting and accept commands.",
    estimateMs: estimateWorkspaceReadyMs,
  },
  waiting_for_terminal: {
    headline: "Connecting the terminal",
    detail: "Opening the first terminal session. Fresh instances can take a little longer here.",
    estimateMs: estimateTerminalReadyMs,
  },
  installing_starter_project: {
    headline: "Setting up your project",
    detail: "Cloning starter project into your workspace.",
    estimateMs: () => 8_000,
  },
  installing_agent: {
    headline: "Installing the agent",
    detail: "Setting up the selected CLI inside the workspace.",
    estimateMs: estimateAgentInstallMs,
  },
  starting_agent: {
    headline: "Starting the agent",
    detail: "Launching the ACP session and checking whether sign-in is required.",
    estimateMs: estimateAgentStartMs,
  },
};

function computeProgressRange(
  stage: BootStage,
  context: Required<BootTimingContext>,
): { progressStart: number; progressEnd: number } {
  const estimates = STAGE_ORDER.map((s) => STAGE_CONFIG[s].estimateMs(context));
  const total = estimates.reduce((sum, ms) => sum + ms, 0);
  const range = BOOT_PROGRESS_END - BOOT_PROGRESS_START;

  const idx = STAGE_ORDER.indexOf(stage);
  let start = BOOT_PROGRESS_START;
  for (let i = 0; i < idx; i++) {
    start += (estimates[i] / total) * range;
  }
  const end = start + (estimates[idx] / total) * range;

  return { progressStart: Math.round(start), progressEnd: Math.round(end) };
}

export function buildBootStage(
  stage: BootStage,
  context: BootTimingContext = {},
  detail?: string,
): BootStageState {
  const config = STAGE_CONFIG[stage];
  const resolvedContext = withDefaults(context);
  const { progressStart, progressEnd } = computeProgressRange(stage, resolvedContext);
  return {
    stage,
    progress: progressStart,
    progressStart,
    progressEnd,
    expectedMs: config.estimateMs(resolvedContext) * 2,
    startedAt: Date.now(),
    headline: config.headline,
    detail: detail || config.detail,
  };
}

export function getBootStageProgress(stage: Pick<
  BootStageState,
  "progressStart" | "progressEnd" | "expectedMs" | "startedAt"
>, now = Date.now()): number {
  const softEnd = Math.max(stage.progressStart, stage.progressEnd - 2);
  if (softEnd <= stage.progressStart) {
    return stage.progressEnd;
  }

  const elapsed = Math.max(0, now - stage.startedAt);
  const ratio = Math.min(1, elapsed / Math.max(stage.expectedMs, 1));

  return stage.progressStart + ((softEnd - stage.progressStart) * ratio);
}

export function defaultHomeForProvider(provider: WorkspaceProvider): string {
  if (provider === "gcp_compute") return "/home";
  if (provider === "e2b") return "/home/user";
  if (provider === "daytona") return "/home/daytona";
  if (provider === "lambda_microvms") return "/home/freshvibe";
  if (provider === "sprites") return "/home/sprite";
  return "/home/daytona";
}

// Re-export from shared source — keep one canonical implementation
import { sanitizeProjectFolderName } from "@/lib/project-utils";
export { sanitizeProjectFolderName };

export function defaultWorkingDirectoryForProject(
  provider: WorkspaceProvider,
  projectName: string,
): string {
  return `${defaultHomeForProvider(provider)}/${sanitizeProjectFolderName(projectName)}`;
}

export function workspaceKey(workspace: Pick<WorkspaceRef, "provider" | "resourceId">): string {
  return `${workspace.provider}:${workspace.resourceId}`;
}
